/**
 * Análise de dados focada em Levantadas de Mão
 * Cada registro representa uma janela de conversão
 */

/**
 * Calcula métricas gerais das levantadas de mão
 * @param {Array} levantadasMao - Dados das levantadas de mão
 * @returns {Object} - Métricas calculadas
 */
export const calculateLevantadasMaoMetrics = (levantadasMao) => {
  if (!levantadasMao || !Array.isArray(levantadasMao) || levantadasMao.length === 0) {
    return {
      totalLevantadasMao: 0,
      levantadasComMensagem: 0,
      levantadasRespondidas: 0,
      levantadasNaoRespondidas: 0,
      taxaEngajamento: 0,
      taxaResposta: 0,
      tempoMedioResposta: 0,
      duracaoMediaJanela: 0,
      totalTickets: 0,
      ticketsPorLevantada: 0,
      leadsMultiplasLevantadas: 0
    };
  }

  const totalLevantadasMao = levantadasMao.length;
  const levantadasComMensagem = levantadasMao.filter(item => item.temMensagemLead).length;
  const levantadasRespondidas = levantadasMao.filter(item => item.foiRespondida).length;
  const levantadasNaoRespondidas = levantadasMao.filter(item => item.naoFoiRespondida).length;
  
  // Taxas
  const taxaEngajamento = totalLevantadasMao > 0 ? (levantadasComMensagem / totalLevantadasMao) * 100 : 0;
  const taxaResposta = levantadasComMensagem > 0 ? (levantadasRespondidas / levantadasComMensagem) * 100 : 0;
  
  // Tempos de resposta
  const temposResposta = levantadasMao
    .filter(item => item.minutosResposta && item.minutosResposta > 0)
    .map(item => item.minutosResposta);
  
  const tempoMedioResposta = temposResposta.length > 0 
    ? temposResposta.reduce((a, b) => a + b, 0) / temposResposta.length 
    : 0;

  // Duração das janelas
  const duracoes = levantadasMao
    .filter(item => item.duracaoJanela && item.duracaoJanela > 0)
    .map(item => item.duracaoJanela);
  
  const duracaoMediaJanela = duracoes.length > 0 
    ? duracoes.reduce((a, b) => a + b, 0) / duracoes.length 
    : 0;

  // Tickets
  const totalTickets = levantadasMao.reduce((total, item) => total + item.totalTickets, 0);
  const ticketsPorLevantada = totalLevantadasMao > 0 ? totalTickets / totalLevantadasMao : 0;

  // Leads com mais de uma levantada de mão (usa telefone como chave)
  const telefoneParaContagem = new Map();
  for (const item of levantadasMao) {
    const telefone = item.telefone || 'NA';
    telefoneParaContagem.set(telefone, (telefoneParaContagem.get(telefone) || 0) + 1);
  }
  const leadsMultiplasLevantadas = Array.from(telefoneParaContagem.values()).filter(qt => qt > 1).length;

  return {
    totalLevantadasMao,
    levantadasComMensagem,
    levantadasRespondidas,
    levantadasNaoRespondidas,
    taxaEngajamento,
    taxaResposta,
    tempoMedioResposta,
    duracaoMediaJanela,
    totalTickets,
    ticketsPorLevantada,
    leadsMultiplasLevantadas
  };
};

/**
 * Analisa performance dos atendentes por levantadas de mão
 * @param {Array} levantadasMao - Dados das levantadas de mão
 * @returns {Array} - Performance por atendente
 */
export const analyzeAtendentesPerformance = (levantadasMao) => {
  if (!levantadasMao || !Array.isArray(levantadasMao)) {
    return [];
  }

  const atendentes = {};
  const stripDomain = (value) => {
    if (!value) return '';
    return value.includes('@') ? value.split('@')[0] : value;
  };
  
  levantadasMao.forEach(item => {
    const atendente = stripDomain(item.atendenteResponsavel) || 'Não atribuído';
    
    if (!atendentes[atendente]) {
      atendentes[atendente] = {
        nome: atendente,
        totalLevantadas: 0,
        levantadasComMensagem: 0,
        levantadasRespondidas: 0,
        levantadasNaoRespondidas: 0,
        totalTickets: 0,
        temposResposta: []
      };
    }
    
    const atendenteData = atendentes[atendente];
    atendenteData.totalLevantadas++;
    atendenteData.totalTickets += item.totalTickets;
    
    if (item.temMensagemLead) {
      atendenteData.levantadasComMensagem++;
      
      if (item.foiRespondida) {
        atendenteData.levantadasRespondidas++;
        if (item.minutosResposta && item.minutosResposta > 0) {
          atendenteData.temposResposta.push(item.minutosResposta);
        }
      } else {
        atendenteData.levantadasNaoRespondidas++;
      }
    }
  });
  
  return Object.values(atendentes)
    .map(atendente => ({
      ...atendente,
      taxaEngajamento: atendente.totalLevantadas > 0 
        ? (atendente.levantadasComMensagem / atendente.totalLevantadas) * 100 
        : 0,
      taxaResposta: atendente.levantadasComMensagem > 0 
        ? (atendente.levantadasRespondidas / atendente.levantadasComMensagem) * 100 
        : 0,
      tempoMedioResposta: atendente.temposResposta.length > 0 
        ? atendente.temposResposta.reduce((a, b) => a + b, 0) / atendente.temposResposta.length 
        : 0,
      ticketsPorLevantada: atendente.totalLevantadas > 0 
        ? atendente.totalTickets / atendente.totalLevantadas 
        : 0
    }))
    .filter(atendente => atendente.nome !== 'Não atribuído' && atendente.totalTickets > 10)
    .sort((a, b) => b.totalLevantadas - a.totalLevantadas);
};

/**
 * Analisa levantadas de mão por horário
 * @param {Array} levantadasMao - Dados das levantadas de mão
 * @returns {Array} - Análise por horário
 */
export const analyzeLevantadasPorHorario = (levantadasMao) => {
  if (!levantadasMao || !Array.isArray(levantadasMao)) {
    return [];
  }

  const porHora = {};
  
  // Inicializa todas as horas
  for (let h = 0; h < 24; h++) {
    porHora[h] = {
      hora: h,
      horaFormatada: `${h.toString().padStart(2, '0')}:00`,
      totalLevantadas: 0,
      levantadasComMensagem: 0,
      levantadasRespondidas: 0,
      levantadasNaoRespondidas: 0,
      totalTickets: 0,
      temposResposta: []
    };
  }
  
  levantadasMao.forEach(item => {
    if (item.levantadaMaoInicio && item.levantadaMaoInicio instanceof Date) {
      const hora = item.levantadaMaoInicio.getHours();
      
      if (hora >= 0 && hora < 24) {
        const dadosHora = porHora[hora];
        dadosHora.totalLevantadas++;
        dadosHora.totalTickets += item.totalTickets;
        
        if (item.temMensagemLead) {
          dadosHora.levantadasComMensagem++;
          
          if (item.foiRespondida) {
            dadosHora.levantadasRespondidas++;
            if (item.minutosResposta && item.minutosResposta > 0) {
              dadosHora.temposResposta.push(item.minutosResposta);
            }
          } else {
            dadosHora.levantadasNaoRespondidas++;
          }
        }
      }
    }
  });
  
  return Object.values(porHora).map(dados => ({
    ...dados,
    taxaEngajamento: dados.totalLevantadas > 0 
      ? (dados.levantadasComMensagem / dados.totalLevantadas) * 100 
      : 0,
    taxaResposta: dados.levantadasComMensagem > 0 
      ? (dados.levantadasRespondidas / dados.levantadasComMensagem) * 100 
      : 0,
    tempoMedioResposta: dados.temposResposta.length > 0 
      ? dados.temposResposta.reduce((a, b) => a + b, 0) / dados.temposResposta.length 
      : 0,
    ticketsPorLevantada: dados.totalLevantadas > 0 
      ? dados.totalTickets / dados.totalLevantadas 
      : 0
  }));
};

/**
 * Analisa distribuição de tempos de resposta por faixas
 * @param {Array} levantadasMao - Dados das levantadas de mão
 * @returns {Object} - Distribuição por faixas de tempo
 */
export const analyzeDistribuicaoTempoResposta = (levantadasMao) => {
  if (!levantadasMao || !Array.isArray(levantadasMao)) {
    return {
      faixasDetalhadas: [],
      agrupamentoEspecial: {
        ate60min: { count: 0, percentage: 0 },
        de61a120min: { count: 0, percentage: 0 },
        acima121min: { count: 0, percentage: 0 }
      },
      totalComTempo: 0,
      totalRespondidas: 0
    };
  }

  const totalRespondidas = levantadasMao.filter(item => item.foiRespondida).length;

  // Filtra apenas levantadas com tempo de resposta válido
  const comTempoResposta = levantadasMao.filter(item => 
    item.foiRespondida && item.minutosResposta && item.minutosResposta > 0
  );

  const totalComTempo = comTempoResposta.length;
  
  if (totalComTempo === 0) {
    return {
      faixasDetalhadas: [],
      agrupamentoEspecial: {
        ate60min: { count: 0, percentage: 0 },
        de61a120min: { count: 0, percentage: 0 },
        acima121min: { count: 0, percentage: 0 }
      },
      totalComTempo: 0,
      totalRespondidas
    };
  }

  // Define faixas de tempo
  const faixas = [
    { min: 0, max: 29, label: '0 a 29 min' },
    { min: 30, max: 59, label: '30 a 59 min' },
    { min: 60, max: 89, label: '60 a 89 min' },
    { min: 90, max: 119, label: '90 a 119 min' },
    { min: 120, max: 149, label: '120 a 149 min' },
    { min: 150, max: 179, label: '150 a 179 min' },
    { min: 180, max: 239, label: '180 a 239 min' },
    { min: 240, max: 299, label: '240 a 299 min' },
    { min: 300, max: 359, label: '300 a 359 min' },
    { min: 360, max: 479, label: '360 a 479 min' },
    { min: 480, max: 719, label: '480 a 719 min' },
    { min: 720, max: 1439, label: '720 a 1439 min' },
    { min: 1440, max: 2879, label: '1440 a 2879 min' },
    { min: 2880, max: Infinity, label: '2880+ min' }
  ];

  // Conta por faixa
  const faixasComContagem = faixas.map(faixa => {
    const count = comTempoResposta.filter(item => {
      const tempo = item.minutosResposta;
      return tempo >= faixa.min && tempo <= faixa.max;
    }).length;
    
    return {
      ...faixa,
      count,
      percentage: totalComTempo > 0 ? (count / totalComTempo) * 100 : 0
    };
  }).filter(faixa => faixa.count > 0);

  // Agrupamento especial
  const ate60min = comTempoResposta.filter(item => item.minutosResposta <= 60).length;
  const de61a120min = comTempoResposta.filter(item => 
    item.minutosResposta > 60 && item.minutosResposta <= 120
  ).length;
  const acima121min = comTempoResposta.filter(item => item.minutosResposta > 120).length;

  return {
    faixasDetalhadas: faixasComContagem,
    agrupamentoEspecial: {
      ate60min: { 
        count: ate60min, 
        percentage: totalComTempo > 0 ? (ate60min / totalComTempo) * 100 : 0 
      },
      de61a120min: { 
        count: de61a120min, 
        percentage: totalComTempo > 0 ? (de61a120min / totalComTempo) * 100 : 0 
      },
      acima121min: { 
        count: acima121min, 
        percentage: totalComTempo > 0 ? (acima121min / totalComTempo) * 100 : 0 
      }
    },
    totalComTempo,
    totalRespondidas
  };
};

/**
 * Analisa janelas de tempo (períodos de 2 horas)
 * @param {Array} levantadasMao - Dados das levantadas de mão
 * @returns {Array} - Análise por janelas de tempo
 */
export const analyzeJanelasDeTempoLevantadas = (levantadasMao) => {
  if (!levantadasMao || !Array.isArray(levantadasMao)) {
    return [];
  }

  const janelas = [];
  
  // Cria janelas de 2 horas
  for (let h = 0; h < 24; h += 2) {
    const horaFim = Math.min(h + 1, 23);
    janelas.push({
      nome: `${h.toString().padStart(2, '0')}:00-${horaFim.toString().padStart(2, '0')}:59`,
      horaInicio: h,
      horaFim: horaFim,
      totalLevantadas: 0,
      levantadasComMensagem: 0,
      levantadasRespondidas: 0,
      totalTickets: 0,
      temposResposta: [],
      isExpediente: h >= 8 && horaFim <= 20
    });
  }
  
  levantadasMao.forEach(item => {
    if (item.levantadaMaoInicio && item.levantadaMaoInicio instanceof Date) {
      const hora = item.levantadaMaoInicio.getHours();
      const janela = janelas.find(j => hora >= j.horaInicio && hora <= j.horaFim);
      
      if (janela) {
        janela.totalLevantadas++;
        janela.totalTickets += item.totalTickets;
        
        if (item.temMensagemLead) {
          janela.levantadasComMensagem++;
          
          if (item.foiRespondida) {
            janela.levantadasRespondidas++;
            if (item.minutosResposta && item.minutosResposta > 0) {
              janela.temposResposta.push(item.minutosResposta);
            }
          }
        }
      }
    }
  });
  
  return janelas.map(janela => ({
    ...janela,
    taxaEngajamento: janela.totalLevantadas > 0 
      ? (janela.levantadasComMensagem / janela.totalLevantadas) * 100 
      : 0,
    taxaResposta: janela.levantadasComMensagem > 0 
      ? (janela.levantadasRespondidas / janela.levantadasComMensagem) * 100 
      : 0,
    tempoMedioResposta: janela.temposResposta.length > 0 
      ? janela.temposResposta.reduce((a, b) => a + b, 0) / janela.temposResposta.length 
      : 0,
    ticketsPorLevantada: janela.totalLevantadas > 0 
      ? janela.totalTickets / janela.totalLevantadas 
      : 0
  }));
};